/**
 * Protected Route Component
 * ==========================
 * Route wrapper that requires authentication and optional permissions
 */

import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth, usePermissions, useOrg } from '../hooks';

/**
 * Protected Route Component
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if authorized
 * @param {string} props.requiredPermission - Optional permission required to access route
 * @param {string[]} props.requiredPermissions - Optional array of permissions (user needs all)
 * @param {string[]} props.anyPermissions - Optional array of permissions (user needs at least one)
 * @param {string} props.requiredRole - Optional role required to access route
 * @param {string[]} props.requiredRoles - Optional array of roles (user needs at least one)
 * @param {boolean} props.matchOrgIdParam - Optional: for non-super-admins, require the route's :orgId param to match their active org
 * @param {string} props.redirectTo - Optional redirect path (default: /login)
 */
const ProtectedRoute = ({
  children,
  requiredPermission,
  requiredPermissions,
  anyPermissions,
  requiredRole,
  requiredRoles,
  superAdminOnly,
  requireOrgAdmin,
  matchOrgIdParam,
  requireOrg = true,
  redirectTo = '/login',
}) => {
  const location = useLocation();
  const params = useParams();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { hasPermission, hasAllPermissions, hasAnyPermission, hasRole, hasAnyRole, isSuperAdmin, isOrgAdmin } = usePermissions();
  const { isOrgSelected, organizations, activeOrg, loading: orgLoading } = useOrg();

  // Show loading state while checking authentication, or while the org context is still
  // resolving the active org. The latter matters after a full-page reload (e.g. switching
  // orgs from the header), where activeOrg starts null and is restored asynchronously from
  // localStorage — without this gate we'd redirect to /select-organization prematurely.
  if (authLoading || (requireOrg && !isSuperAdmin && orgLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Super admin only check
  if (superAdminOnly && !isSuperAdmin) {
    return <PermissionDenied />;
  }

  // Org admin check
  if (requireOrgAdmin && !isOrgAdmin) {
    return <PermissionDenied />;
  }

  // Org id param check: non-super-admins may only access their own org's route
  if (matchOrgIdParam && !isSuperAdmin && params.orgId !== activeOrg?.id) {
    return <PermissionDenied />;
  }

  // Org selection check
  if (requireOrg && !isSuperAdmin && !isOrgSelected && organizations.length > 1) {
    return <Navigate to="/select-organization" state={{ from: location }} replace />;
  }

  // Check single permission
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <PermissionDenied />;
  }

  // Check all permissions (user must have all)
  if (requiredPermissions && !hasAllPermissions(requiredPermissions)) {
    return <PermissionDenied />;
  }

  // Check any permissions (user must have at least one)
  if (anyPermissions && !hasAnyPermission(anyPermissions)) {
    return <PermissionDenied />;
  }

  // Check single role
  if (requiredRole && !isSuperAdmin && !hasRole(requiredRole)) {
    return <PermissionDenied />;
  }

  // Check any roles (user must have at least one)
  if (requiredRoles && !isSuperAdmin && !hasAnyRole(requiredRoles)) {
    return <PermissionDenied />;
  }

  // User is authenticated and authorized
  return children;
};

/**
 * Permission Denied Component
 */
const PermissionDenied = () => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-strong p-8 text-center">
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
          <svg
            className="h-8 w-8 text-red-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h2 className="text-2xl font-sans font-semibold text-gray-900 dark:text-white mb-2">Access Denied</h2>
        <p className="text-gray-600 mb-6">
          You don't have permission to access this page. Please contact your administrator if you believe this is an error.
        </p>
        <a
          href="/dashboard"
          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-primary hover:bg-primary-600 transition-colors shadow-medium"
        >
          Go to Dashboard
        </a>
      </div>
    </div>
  );
};

export default ProtectedRoute;

